'use client';

import { c as _c } from "react/compiler-runtime";
import Player from '@vimeo/player';
import clsx from 'clsx';
import { FC, useEffect, useRef, useState } from 'react';
import { preconnect } from 'react-dom';
import cn from '@/helpers/cn';
import css from './style.module.scss';
export interface Props {
  fitCover?: boolean;
  autopause?: boolean;
  background?: boolean;
  className?: string;
  loop?: boolean;
  muted?: boolean;
  playing?: boolean;
  responsive?: boolean;
  thumbnailUrl?: string;
  url: string;
  preload?: 'auto' | 'metadata' | 'metadata_on_hover' | 'auto_on_hover' | 'none';
  controls?: boolean;
  disablePointerEvents?: boolean;
  thumbnailLoading?: 'lazy' | 'eager';
}
const extractVimeoId = (url: string): string | null => {
  const match = url.match(/vimeo\.com\/(?:manage\/videos\/)?(\d+)/);
  return match ? match[1] : null;
};
const VimeoPlayer: FC<Props> = t0 => {
  const $ = _c(38);
  const {
    autopause: t1,
    fitCover,
    thumbnailUrl,
    background: t2,
    className: t3,
    loop: t4,
    muted: t5,
    playing: t6,
    responsive: t7,
    url,
    preload,
    controls: t8,
    disablePointerEvents: t9,
    thumbnailLoading: t10
  } = t0;
  const autopause = t1 === undefined ? false : t1;
  const background = t2 === undefined ? true : t2;
  const className = t3 === undefined ? "" : t3;
  const loop = t4 === undefined ? true : t4;
  const muted = t5 === undefined ? false : t5;
  const playing = t6 === undefined ? true : t6;
  const responsive = t7 === undefined ? true : t7;
  const controls = t8 === undefined ? false : t8;
  const disablePointerEvents = t9 === undefined ? true : t9;
  const thumbnailLoading = t10 === undefined ? "lazy" : t10;
  preconnect("https://player.vimeo.com");
  preconnect("https://f.vimeocdn.com");
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const canPlayRef = useRef(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [showCoverImage, setShowCoverImage] = useState(!!thumbnailUrl);
  const [shouldInit, setShouldInit] = useState(false);
  let t11;
  let t12;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      const el = wrapperRef.current;
      if (!el) {
        return;
      }
      const observer = new IntersectionObserver(t13 => {
        const [entry] = t13;
        if (entry.isIntersecting) {
          setShouldInit(true);
          observer.disconnect();
        }
      }, {
        rootMargin: "400px"
      });
      observer.observe(el);
      return () => observer.disconnect();
    };
    t12 = [];
    $[0] = t11;
    $[1] = t12;
  } else {
    t11 = $[0];
    t12 = $[1];
  }
  useEffect(t11, t12);
  let t13;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = async () => {
      if (!wrapperRef.current || !containerRef.current || !playerRef.current) {
        return;
      }
      const [width, height] = await Promise.all([playerRef.current.getVideoWidth(), playerRef.current.getVideoHeight()]);
      if (!wrapperRef.current || !containerRef.current) {
        return;
      }
      const wrapperAspectRatio = wrapperRef.current.clientWidth / wrapperRef.current.clientHeight;
      const defaultVideoAspectRatio = width / height;
      const scale = Math.max(defaultVideoAspectRatio / wrapperAspectRatio, wrapperAspectRatio / defaultVideoAspectRatio);
      containerRef.current.style.transform = `scale(${scale})`;
    };
    $[2] = t13;
  } else {
    t13 = $[2];
  }
  const fitToAspectRatio = t13;
  let t14;
  if ($[3] !== autopause || $[4] !== background || $[5] !== controls || $[6] !== fitCover || $[7] !== loop || $[8] !== muted || $[9] !== playing || $[10] !== preload || $[11] !== responsive || $[12] !== shouldInit || $[13] !== url) {
    t14 = () => {
      if (containerRef.current === null || shouldInit === false) {
        return;
      }
      const vimeoId = extractVimeoId(url);
      if (!vimeoId) {
        console.error("Invalid Vimeo URL:", url);
        return;
      }
      const numericVimeoId = parseInt(vimeoId, 10);
      if (isNaN(numericVimeoId)) {
        throw new Error(`Invalid Vimeo ID: ${vimeoId}`);
      }
      const player = new Player(containerRef.current, {
        id: numericVimeoId,
        loop,
        muted,
        background: controls ? false : background,
        responsive,
        colors: controls ? ["000000", "9bff00", "FFFFFF", "000000"] : undefined,
        autopause,
        autoplay: playing,
        preload,
        controls,
        title: false,
        byline: false,
        portrait: false,
        dnt: true
      });
      playerRef.current = player;
      if (fitCover) {
        fitToAspectRatio();
      }
      player.on("play", async () => {
        if (background && !canPlayRef.current) {
          await player.pause();
          canPlayRef.current = true;
        }
        setIsPlayerReady(true);
        if (!controls) {
          setShowCoverImage(false);
        }
      });
      if (!controls) {
        player.on("pause", () => {
          setShowCoverImage(true);
          setTimeout(async () => {
            const isPaused = await player?.getPaused();
            if (isPaused) {
              player.setCurrentTime(0);
            }
          }, 500);
        });
      }
      return () => {
        player.destroy();
      };
    };
    $[3] = autopause;
    $[4] = background;
    $[5] = controls;
    $[6] = fitCover;
    $[7] = loop;
    $[8] = muted;
    $[9] = playing;
    $[10] = preload;
    $[11] = responsive;
    $[12] = shouldInit;
    $[13] = url;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  let t15;
  if ($[15] !== controls || $[16] !== loop || $[17] !== muted || $[18] !== shouldInit || $[19] !== url) {
    t15 = [url, loop, muted, shouldInit, controls];
    $[15] = controls;
    $[16] = loop;
    $[17] = muted;
    $[18] = shouldInit;
    $[19] = url;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[21] !== isPlayerReady || $[22] !== playing) {
    t16 = () => {
      if (playerRef.current === null || isPlayerReady === false || playing === undefined) {
        return;
      }
      if (playing) {
        playerRef.current.play().catch(_temp);
      } else {
        playerRef.current.pause().catch(_temp2);
      }
    };
    t17 = [playing, isPlayerReady];
    $[21] = isPlayerReady;
    $[22] = playing;
    $[23] = t16;
    $[24] = t17;
  } else {
    t16 = $[23];
    t17 = $[24];
  }
  useEffect(t16, t17);
  let t18;
  if ($[25] !== className) {
    t18 = cn(css.wrapper, className);
    $[25] = className;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const t19 = showCoverImage ? 0 : 1;
  let t20;
  if ($[27] !== t19) {
    t20 = <div ref={containerRef} className={css.player} style={{
      opacity: t19
    }} />;
    $[27] = t19;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  let t21;
  if ($[29] !== disablePointerEvents || $[30] !== showCoverImage || $[31] !== thumbnailLoading || $[32] !== thumbnailUrl) {
    t21 = thumbnailUrl ? <img alt="" decoding="async" draggable={false} fetchPriority={thumbnailLoading === "eager" ? "high" : "low"} loading={thumbnailLoading} src={thumbnailUrl} className={clsx(css.thumbnail, disablePointerEvents && css.disablePointerEvents)} style={{
      opacity: showCoverImage ? 1 : 0
    }} /> : null;
    $[29] = disablePointerEvents;
    $[30] = showCoverImage;
    $[31] = thumbnailLoading;
    $[32] = thumbnailUrl;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  let t22;
  if ($[34] !== t18 || $[35] !== t20 || $[36] !== t21) {
    t22 = <div ref={wrapperRef} className={t18}>{t20}{t21}</div>;
    $[34] = t18;
    $[35] = t20;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  return t22;
};
export default VimeoPlayer;
function _temp(error) {
  console.warn("Failed to play video:", error.name);
}
function _temp2(error_0) {
  console.warn("Failed to pause video:", error_0.name);
}